import React, { createContext, useContext, useState, useEffect } from "react";

// Create the AudioContext
const AudioContext = createContext();

// Create a provider component
export const AudioProvider = ({
  children,
  soundFile,
  beepFile,
  hoverFile,
  waterFile,
  popFile,
  flipFile,
  rotateFile,
  openFile,
}) => {
  const [audio, setAudio] = useState(null);
  const [beep, setBeep] = useState(null);
  const [hover, setHover] = useState(null);
  const [water, setWater] = useState(null);
  const [pop, setPop] = useState(null);
  const [flip, setFlip] = useState(null);
  const [rotate, setRotate] = useState(null);
  const [open, setOpen] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const audioElement = new Audio(soundFile);
    setAudio(audioElement);

    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [soundFile]);

  useEffect(() => {
    const beepElement = new Audio(beepFile);
    setBeep(beepElement);

    return () => {
      if (beepElement) {
        beepElement.pause();
        beepElement.currentTime = 0;
      }
    };
  }, [beepFile]);

  useEffect(() => {
    const hoverElement = new Audio(hoverFile);
    setHover(hoverElement);

    return () => {
      if (hoverElement) {
        hoverElement.pause();
        hoverElement.currentTime = 0;
      }
    };
  }, [hoverFile]);

  useEffect(() => {
    const waterElement = new Audio(waterFile);
    setWater(waterElement);

    return () => {
      if (waterElement) {
        waterElement.pause();
        waterElement.currentTime = 0;
      }
    };
  }, [waterFile]);

  useEffect(() => {
    const popElement = new Audio(popFile);
    setPop(popElement);

    return () => {
      if (popElement) {
        popElement.pause();
        popElement.currentTime = 0;
      }
    };
  }, [popFile]);

  useEffect(() => {
    const flipElement = new Audio(flipFile);
    setFlip(flipElement);

    return () => {
      if (flipElement) {
        flipElement.pause();
        flipElement.currentTime = 0;
      }
    };
  }, [flipFile]);

  useEffect(() => {
    const openElement = new Audio(openFile);
    setOpen(openElement);

    return () => {
      if (openElement) {
        openElement.pause();
        openElement.currentTime = 0;
      }
    };
  }, [openFile]);

  useEffect(() => {
    const rotateElement = new Audio(rotateFile);
    setRotate(rotateElement);

    return () => {
      if (rotateElement) {
        rotateElement.pause();
        rotateElement.currentTime = 0;
      }
    };
  }, [rotateFile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const playAudio = () => {
    if (audio && audio.paused) {
      audio.volume = 0.6;
      audio.play();
    }
  };

  const pauseAudio = () => {
    if (audio && !audio.paused) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  const playBeep = () => {
    if (beep && beep.paused) {
      beep.volume = 0.3;
      beep.play();
    }
  };

  const pauseBeep = () => {
    if (beep && !beep.paused) {
      beep.pause();
      beep.currentTime = 0;
    }
  };

  const playHover = (volume) => {
    if (hover && hover.paused) {
      hover.volume = volume;
      hover.play();
    }
  };

  const pauseHover = () => {
    if (hover && !hover.paused) {
      hover.pause();
      hover.currentTime = 0;
    }
  };

  const playWater = (volume) => {
    if (water && water.paused) {
      water.volume = volume;
      water.play();
    }
  };

  const pauseWater = () => {
    if (water && !water.paused) {
      water.pause();
      water.currentTime = 0;
    }
  };

  const playPop = (volume) => {
    if (pop && pop.paused) {
      pop.volume = volume;
      pop.play();
    }
  };

  const pausePop = () => {
    if (pop && !pop.paused) {
      pop.pause();
      pop.currentTime = 0;
    }
  };

  const playFlip = (volume) => {
    if (flip && flip.paused) {
      flip.volume = volume;
      flip.play();
    }
  };

  const pauseFlip = () => {
    if (flip && !flip.paused) {
      flip.pause();
      flip.currentTime = 0;
    }
  };

  const playRotate = (volume) => {
    if (rotate && rotate.paused) {
      rotate.volume = volume;
      rotate.play();
    }
  };

  const pauseRotate = () => {
    if (rotate && !rotate.paused) {
      rotate.pause();
      rotate.currentTime = 0;
    }
  };

  const playOpen = (volume) => {
    if (open && open.paused) {
      open.volume = volume;
      open.play();
    }
  };

  const pauseOpen = () => {
    if (open && !open.paused) {
      open.pause();
      open.currentTime = 0;
    }
  };

  return (
    <AudioContext.Provider
      value={{
        audio,
        playAudio,
        pauseAudio,
        beep,
        playBeep,
        pauseBeep,
        hover,
        playHover,
        pauseHover,
        water,
        playWater,
        pauseWater,
        pop,
        playPop,
        pausePop,
        flip,
        playFlip,
        pauseFlip,
        rotate,
        playRotate,
        pauseRotate,
        open,
        playOpen,
        pauseOpen,
        isMobile,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

// Custom hook to consume the context
export const useAudio = () => {
  return useContext(AudioContext);
};
